import { ChevronDown } from 'lucide-react';
import { useId } from 'react';

import type { ReactElement, ReactNode } from 'react';

export interface AccordionSection<Id extends string> {
    id: Id;
    title: string;
    content: ReactNode;
}

/**
 * Controlled exclusive accordion following the WAI-ARIA accordion pattern. The
 * open section is a single value, so at most one section can be expanded and
 * `null` collapses them all; every section stays in the DOM so the ids used
 * by `aria-controls` remain resolvable while collapsed.
 */
export function Accordion<Id extends string>({
    sections,
    openSection,
    onOpenSectionChange,
}: {
    sections: readonly AccordionSection<Id>[];
    openSection: Id | null;
    onOpenSectionChange: (section: Id | null) => void;
}): ReactElement {
    const baseId = useId();
    return (
        <div className='accordion'>
            {sections.map((section) => {
                const isOpen = section.id === openSection;
                const triggerId = `${baseId}-trigger-${section.id}`;
                const panelId = `${baseId}-panel-${section.id}`;
                return (
                    <div
                        key={section.id}
                        className='accordion__section'
                    >
                        <h3 className='accordion__heading'>
                            <button
                                id={triggerId}
                                className='accordion__trigger'
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => {
                                    onOpenSectionChange(isOpen ? null : section.id);
                                }}
                            >
                                <span>{section.title}</span>
                                <ChevronDown aria-hidden='true' />
                            </button>
                        </h3>
                        <div
                            id={panelId}
                            className='accordion__panel'
                            role='region'
                            aria-labelledby={triggerId}
                            hidden={!isOpen}
                        >
                            {section.content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
